import { useState } from 'react';
import { Download, FileSpreadsheet, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { VisibilityState } from '@tanstack/react-table';
import { Button } from '@/core/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/core/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';

interface ExportMenuProps {
  columnVisibility: VisibilityState;
  availableColumns: Array<{
    id: string;
    header: string;
  }>;
  onExport: (columnVisibility: VisibilityState) => void;
  disabled?: boolean;
}

/**
 * Export menu component with dropdown options for exporting data
 * Provides two options:
 * - Excel - Selected Columns: Opens column selection dialog
 * - Excel - All Columns: Exports all columns immediately
 */
export const ExportMenu = ({
  columnVisibility,
  availableColumns,
  onExport,
  disabled = false,
}: ExportMenuProps) => {
  const [isColumnDialogOpen, setIsColumnDialogOpen] = useState(false);
  const [exportColumnVisibility, setExportColumnVisibility] = useState<VisibilityState>({});

  // Handle "Excel - Selected Columns" click
  const handleExportSelectedColumns = () => {
    // Initialize with current column visibility
    setExportColumnVisibility({ ...columnVisibility });
    setIsColumnDialogOpen(true);
  };

  // Handle "Excel - All Columns" click
  const handleExportAllColumns = () => {
    const allColumnsVisible: VisibilityState = {};
    availableColumns.forEach(col => {
      allColumnsVisible[col.id] = true;
    });
    onExport(allColumnsVisible);
  };

  // Toggle column visibility in the dialog
  const toggleColumn = (columnId: string) => {
    setExportColumnVisibility(prev => ({
      ...prev,
      [columnId]: prev[columnId] === false ? true : prev[columnId] === true ? false : !columnVisibility[columnId],
    }));
  };

  // Check if column is visible in export selection
  const isColumnVisible = (columnId: string): boolean => {
    if (exportColumnVisibility[columnId] !== undefined) {
      return exportColumnVisibility[columnId] !== false;
    }
    return columnVisibility[columnId] !== false;
  };

  // Count selected columns
  const selectedCount = availableColumns.filter(col => isColumnVisible(col.id)).length;

  // Handle export from dialog
  const handleDialogExport = () => {
    // Build final visibility state
    const finalVisibility: VisibilityState = {};
    availableColumns.forEach(col => {
      finalVisibility[col.id] = isColumnVisible(col.id);
    });
    onExport(finalVisibility);
    setIsColumnDialogOpen(false);
  };

  // Select all columns
  const selectAllColumns = () => {
    const allVisible: VisibilityState = {};
    availableColumns.forEach(col => {
      allVisible[col.id] = true;
    });
    setExportColumnVisibility(allVisible);
  };

  // Deselect all columns
  const deselectAllColumns = () => {
    const allHidden: VisibilityState = {};
    availableColumns.forEach(col => {
      allHidden[col.id] = false;
    });
    setExportColumnVisibility(allHidden);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
            disabled={disabled}
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem
            onClick={handleExportSelectedColumns}
            className="cursor-pointer"
          >
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            <span>Excel - Selected Columns</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleExportAllColumns}
            className="cursor-pointer"
          >
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            <span>Excel - All Columns</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Column Selection Dialog */}
      <Dialog open={isColumnDialogOpen} onOpenChange={setIsColumnDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Columns to Export</DialogTitle>
            <DialogDescription>
              Choose which columns to include in the Excel export.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {/* Select All / Deselect All */}
            <div className="flex space-x-2 mb-4">
              <Button
                size="sm"
                variant="outline"
                onClick={selectAllColumns}
                className="text-xs flex-1"
              >
                Select All
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={deselectAllColumns}
                className="text-xs flex-1"
              >
                Deselect All
              </Button>
            </div>

            {/* Column List */}
            <div className="max-h-64 overflow-y-auto border rounded-md">
              {availableColumns.map((column) => {
                const isVisible = isColumnVisible(column.id);

                return (
                  <div
                    key={column.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    onClick={() => toggleColumn(column.id)}
                  >
                    <div className="flex items-center space-x-2">
                      {isVisible ? (
                        <Eye className="h-4 w-4 text-blue-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                      <span className={`text-sm ${isVisible ? 'text-gray-900' : 'text-gray-500'}`}>
                        {column.header}
                      </span>
                    </div>

                    <div
                      className={`w-4 h-4 rounded border-2 ${
                        isVisible
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      } flex items-center justify-center`}
                    >
                      {isVisible && (
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 8 8">
                          <path d="M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Validation message */}
            {selectedCount === 0 && (
              <p className="text-sm text-red-500 mt-2">
                Please select at least one column to export.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsColumnDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDialogExport}
              disabled={selectedCount === 0}
            >
              Export ({selectedCount} columns)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
